import { useCallback, useEffect, useRef, useState } from 'react'
import { Activity, CalendarDays, CheckCheck, ChevronDown, ChevronLeft, ChevronRight, Clock3, Download, FileSpreadsheet, FileUp, History, ListFilter, LocateFixed, MapPinned, Pencil, RefreshCw, RotateCcw, Save, Search, Trash2, TriangleAlert, Undo2, X } from 'lucide-react'
import { api, apiFile, ApiError } from '../api'
import { Button, EmptyState, Modal, Status } from '../components'
import { validateImportedRow } from '../../shared/normalization'
import type { Order } from '../types'
import { DEFAULT_ORDER_EXPORT_FIELDS, ORDER_EXPORT_FIELDS, ORDER_EXPORT_PRESETS, type OrderExportFieldId } from '../../shared/order-export'
import { parseImportFile } from '../import-file'

type DuplicateReview = {
  id: string
  reviewType: string
  primaryName: string
  primaryPhone: string
  primaryAddress: string
  relatedName: string
  relatedPhone: string
  relatedAddress: string
}

type DeliveryAttempt = {
  id: string
  outcome: 'DELIVERED' | 'NOT_FOUND'
  receiverName?: string
  receiverRelationship?: string
  notes?: string
  attemptedAt: string
  hasPhoto: number
  proofUrl?: string
}

type SyncStatus = {
  configured: boolean
  lastRunAt: string | null
  lastSuccessAt: string | null
  lastError: string | null
  lastResult: { inserted: number; invalid: number; remaining: number } | null
}

type ImportRun = {
  id: string
  sourceType: 'CSV_UPLOAD' | 'JSON_UPLOAD'
  sourceRef: string | null
  sourceModifiedAt: string | null
  rowCount: number
  insertedCount: number
  mergedCount: number
  reviewCount: number
  ignoredCount: number
  status: 'PREVIEW' | 'RUNNING' | 'COMPLETED' | 'FAILED'
  errorMessage: string | null
  importedByName: string | null
  createdAt: string
  completedAt: string | null
  rolledBackAt: string | null
  rollbackReason: string | null
  rollbackCancelledCount: number
  rolledBackByName: string | null
}

type ImportRollbackPreview = {
  id: string
  sourceRef: string | null
  status: string
  rolledBackAt: string | null
  createdOrders: number
  mergedOrders: number
  ignoredRows: number
  canCancel: number
  alreadyCancelled: number
  protectedOrders: number
}

type OrderLocationState = {
  state: string
  total: number
  cities: Array<{ city: string; total: number }>
}

const STATE_LABELS: Record<string, string> = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará', DF: 'Distrito Federal',
  ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
  PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins',
}

type NeighborhoodOverview = {
  city: string
  neighborhood: string
  total: number
  delivered: number
  pending: number
  today: number
}

type RegionOverview = {
  key: string
  label: string
  total: number
  delivered: number
  pending: number
  today: number
  neighborhoods: NeighborhoodOverview[]
}

type OrdersOverview = {
  generatedAt: string
  localDate: string
  today: { received: number; delivered: number }
  period: { from: string; to: string; received: number; delivered: number; pending: number }
  base: { total: number; delivered: number; pending: number }
  regions: RegionOverview[]
}

type PeriodPreset = 'today' | 'yesterday' | 'sevenDays' | 'thirtyDays' | 'custom'
type PeriodRange = { from: string; to: string }

const PERIOD_OPTIONS: Array<{ id: PeriodPreset; label: string }> = [
  { id: 'today', label: 'Hoje' },
  { id: 'yesterday', label: 'Ontem' },
  { id: 'sevenDays', label: '7 dias' },
  { id: 'thirtyDays', label: '30 dias' },
  { id: 'custom', label: 'Personalizado' },
]

function rioDate(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo' }).format(new Date())
}

function shiftDate(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function rangeForPreset(preset: Exclude<PeriodPreset, 'custom'>): PeriodRange {
  const today = rioDate()
  if (preset === 'yesterday') {
    const yesterday = shiftDate(today, -1)
    return { from: yesterday, to: yesterday }
  }
  if (preset === 'sevenDays') return { from: shiftDate(today, -6), to: today }
  if (preset === 'thirtyDays') return { from: shiftDate(today, -29), to: today }
  return { from: today, to: today }
}

function formatPeriod(range: PeriodRange): string {
  const format = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  return range.from === range.to ? format(range.from) : `${format(range.from)} a ${format(range.to)}`
}

type OrderDetail = Order & {
  source?: string
  page?: string
  consent: number
  nextEligibleAt?: string
  deletedByName?: string | null
  statusBeforeDeletion?: string | null
}

type OrderEditForm = {
  recipientName: string
  phone: string
  cep: string
  street: string
  streetNumber: string
  complement: string
  neighborhood: string
  city: string
  state: string
  referencePoint: string
  quantity: string
  stickerModels: string
}

const relationshipLabels: Record<string, string> = {
  SELF: 'A própria pessoa',
  FAMILY: 'Familiar',
  DOORMAN: 'Porteiro',
  NEIGHBOR: 'Vizinho',
  OTHER: 'Outra pessoa',
}

function sourceLabel(value?: string): string {
  if (value === 'video_protecao_infantil') return 'Proteção Infantil'
  if (value === 'planilha_redes_sociais') return 'Planilha · Redes sociais'
  if (value === 'planilha_call_center') return 'Planilha · Call center'
  return value || 'Origem não informada'
}

function stickerModels(value: string): string[] {
  try { return (JSON.parse(value || '[]') as unknown[]).map(String).filter(Boolean) } catch { return [] }
}

function editFormFromOrder(order: OrderDetail): OrderEditForm {
  return {
    recipientName: order.recipientName,
    phone: order.phone,
    cep: order.cep,
    street: order.street,
    streetNumber: order.streetNumber,
    complement: order.complement ?? '',
    neighborhood: order.neighborhood,
    city: order.city,
    state: order.state,
    referencePoint: order.referencePoint ?? '',
    quantity: String(order.quantity),
    stickerModels: stickerModels(order.stickerModelsJson).join(', '),
  }
}

function bytesToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let value = ''
  bytes.forEach((byte) => { value += String.fromCharCode(byte) })
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function formatMoment(value: string | null): string {
  if (!value) return 'nunca'
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return 'nunca'
  return moment.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

function paginationPages(current: number, total: number): number[] {
  const visible = Math.min(5, total)
  const start = Math.max(1, Math.min(current - Math.floor(visible / 2), total - visible + 1))
  return Array.from({ length: visible }, (_, index) => start + index)
}

/**
 * Torna visível o que antes só existia no banco: se a entrada automática de
 * pedidos está de pé, quando rodou pela última vez e o que ela trouxe.
 */
function SyncBanner({ state }: { state: SyncStatus | null }) {
  if (!state) return null
  if (!state.configured) {
    return <div className="notice sync-notice">Importe os pedidos da Tainá por planilha CSV, XLSX ou XLS. Nenhum pedido de outra campanha será importado automaticamente.</div>
  }
  if (state.lastError) {
    return <div className="notice sync-notice sync-notice-warning">Última sincronização falhou em {formatMoment(state.lastRunAt)}: {state.lastError}. Os pedidos já importados continuam intactos.</div>
  }
  const pending = state.lastResult?.remaining ?? 0
  const invalid = state.lastResult?.invalid ?? 0
  const extra = [
    pending ? `${pending.toLocaleString('pt-BR')} na fila para a próxima rodada` : '',
    invalid ? `${invalid.toLocaleString('pt-BR')} aguardando correção de endereço` : '',
  ].filter(Boolean).join(' · ')
  return (
    <div className="notice sync-notice">
      Entrada automática ativa, a cada 5 minutos. Última sincronização: {formatMoment(state.lastSuccessAt)}
      {extra ? ` · ${extra}` : ''}
    </div>
  )
}

function ImportHistoryRows({ imports, onView, onRollback }: { imports: ImportRun[]; onView: (run: ImportRun, reviewOnly: boolean) => void; onRollback: (run: ImportRun) => void }) {
  return <div className="import-history-list">
    {imports.map((run) => {
      const processed = run.insertedCount + run.mergedCount + run.ignoredCount
      const linkedOrders = run.insertedCount + run.mergedCount
      return <article className={`import-history-row import-history-${run.status.toLowerCase()}`} key={run.id}>
        <div className="import-history-file">
          <span className="import-history-icon">{run.status === 'FAILED' ? <TriangleAlert size={16} /> : <FileSpreadsheet size={16} />}</span>
          <div><strong>{run.sourceRef || 'Importação manual'}</strong><span>{formatMoment(run.completedAt ?? run.createdAt)}{run.importedByName ? ` · ${run.importedByName}` : ''}</span></div>
        </div>
        <div className="import-history-status">{run.rolledBackAt ? <span className="import-rollback-status"><Undo2 size={12} /> Desfeita</span> : <Status value={run.status} />}{run.errorMessage ? <small title={run.errorMessage}>{run.errorMessage}</small> : null}</div>
        <div className="import-history-counts" aria-label="Resultado da importação">
          <span><strong>{processed.toLocaleString('pt-BR')}</strong> processadas</span>
          <span><strong>{run.insertedCount.toLocaleString('pt-BR')}</strong> novas</span>
          <span><strong>{run.mergedCount.toLocaleString('pt-BR')}</strong> mescladas</span>
          <span className={run.reviewCount ? 'has-review' : ''}><strong>{run.reviewCount.toLocaleString('pt-BR')}</strong> revisar</span>
        </div>
        <div className="import-history-actions">
          <button type="button" disabled={!linkedOrders} onClick={() => onView(run, false)}><ListFilter size={13} /> Ver pedidos</button>
          {run.reviewCount ? <button type="button" className="review" onClick={() => onView(run, true)}>Revisar</button> : null}
          {run.status === 'COMPLETED' && !run.rolledBackAt ? <button type="button" className="rollback" onClick={() => onRollback(run)}><Undo2 size={13} /> Desfazer</button> : null}
        </div>
      </article>
    })}
  </div>
}

function ImportHistoryPanel({ imports, loading, error, onView, onRollback, onOpenAll }: {
  imports: ImportRun[]
  loading: boolean
  error: string
  onView: (run: ImportRun, reviewOnly: boolean) => void
  onRollback: (run: ImportRun) => void
  onOpenAll: () => void
}) {
  return <section className="import-history-panel" aria-labelledby="import-history-title">
    <header>
      <div className="import-history-heading"><span><History size={15} /></span><div><h2 id="import-history-title">Histórico de importações</h2><p>Confirmação permanente das planilhas enviadas.</p></div></div>
      {imports.length > 3 ? <button type="button" className="import-history-all" onClick={onOpenAll}>Ver histórico completo</button> : null}
    </header>
    {loading ? <div className="import-history-loading"><span /><span /></div> : null}
    {!loading && error ? <div className="import-history-empty">{error}</div> : null}
    {!loading && !error && imports.length ? <ImportHistoryRows imports={imports.slice(0, 3)} onView={onView} onRollback={onRollback} /> : null}
    {!loading && !error && !imports.length ? <div className="import-history-empty">Nenhuma planilha importada ainda.</div> : null}
  </section>
}

function OverviewPanel({ overview, selectedKey, expanded, refreshing, periodPreset, periodRange, customFrom, customTo, onSelectRegion, onToggleExpanded, onRefresh, onNeighborhood, onPreset, onCustomFrom, onCustomTo, onApplyCustom }: {
  overview: OrdersOverview | null
  selectedKey: string
  expanded: boolean
  refreshing: boolean
  periodPreset: PeriodPreset
  periodRange: PeriodRange
  customFrom: string
  customTo: string
  onSelectRegion: (key: string) => void
  onToggleExpanded: () => void
  onRefresh: () => void
  onNeighborhood: (item: NeighborhoodOverview) => void
  onPreset: (preset: PeriodPreset) => void
  onCustomFrom: (value: string) => void
  onCustomTo: (value: string) => void
  onApplyCustom: () => void
}) {
  if (!overview) return <section className="orders-overview orders-overview-loading"><span /><span /><span /></section>
  const selected = overview.regions.find((region) => region.key === selectedKey) ?? overview.regions.find((region) => region.total > 0) ?? overview.regions[0]
  const visibleNeighborhoods = expanded ? selected.neighborhoods : selected.neighborhoods.slice(0, 8)
  const largest = Math.max(1, ...selected.neighborhoods.map((item) => item.total))
  const updated = new Date(overview.generatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return <section className="orders-overview" aria-label="Visão rápida dos pedidos em tempo real">
    <header className="orders-overview-header">
      <div><span className="orders-live-label"><i /> Tempo real</span><h2>Pulso dos pedidos</h2><p>Entrada, entregas e concentração geográfica da operação.</p></div>
      <button className="orders-overview-refresh" onClick={onRefresh} disabled={refreshing}><RefreshCw className={refreshing ? 'spin' : ''} size={14} /> Atualizado {updated}</button>
    </header>
    <div className="orders-period-bar">
      <div className="orders-period-presets" role="group" aria-label="Período da visão de pedidos">
        <span><CalendarDays size={13} /> Período</span>
        {PERIOD_OPTIONS.map((option) => <button key={option.id} type="button" aria-pressed={periodPreset === option.id} className={periodPreset === option.id ? 'active' : ''} onClick={() => onPreset(option.id)}>{option.label}</button>)}
      </div>
      {periodPreset === 'custom' ? <div className="orders-custom-period">
        <label><span>De</span><input aria-label="Data inicial do período" type="date" value={customFrom} max={customTo || undefined} onChange={(event) => onCustomFrom(event.target.value)} /></label>
        <label><span>Até</span><input aria-label="Data final do período" type="date" value={customTo} min={customFrom || undefined} onChange={(event) => onCustomTo(event.target.value)} /></label>
        <button type="button" onClick={onApplyCustom} disabled={!customFrom || !customTo || customFrom > customTo}>Aplicar</button>
      </div> : null}
      <small>Exibindo {formatPeriod(periodRange)}</small>
    </div>
    <div className="orders-today-strip">
      <article className="orders-today-primary"><span><Clock3 size={15} /> Pedidos no período</span><strong>{overview.period.received.toLocaleString('pt-BR')}</strong><small>{formatPeriod(overview.period)}</small></article>
      <article><span><CheckCheck size={15} /> Entregues no período</span><strong>{overview.period.delivered.toLocaleString('pt-BR')}</strong><small>Confirmações feitas no intervalo</small></article>
      <article><span><Activity size={15} /> Aguardando no recorte</span><strong>{overview.period.pending.toLocaleString('pt-BR')}</strong><small>Pedidos deste período ainda pendentes</small></article>
      <article><span><MapPinned size={15} /> Base monitorada</span><strong>{overview.base.total.toLocaleString('pt-BR')}</strong><small>{overview.base.delivered.toLocaleString('pt-BR')} já entregues</small></article>
    </div>
    <div className="orders-region-heading"><div><strong>Regiões e bairros no período</strong><span>Clique em uma região para abrir a distribuição do recorte.</span></div><small>{overview.regions.filter((region) => region.total > 0).length} regiões com pedidos</small></div>
    <div className="orders-region-tabs" role="tablist" aria-label="Regiões dos pedidos">
      {overview.regions.map((region) => <button key={region.key} role="tab" aria-selected={selected.key === region.key} className={selected.key === region.key ? 'active' : ''} onClick={() => onSelectRegion(region.key)}>
        <span>{region.label}</span><strong>{region.total.toLocaleString('pt-BR')}</strong><small>{region.pending.toLocaleString('pt-BR')} pendentes</small>
      </button>)}
    </div>
    <div className="orders-neighborhood-panel">
      <header><div><strong>{selected.label}</strong><span>{selected.total.toLocaleString('pt-BR')} pedidos no período · {selected.pending.toLocaleString('pt-BR')} aguardando entrega</span></div><small>{selected.neighborhoods.length} bairros/localidades</small></header>
      <div className="orders-neighborhood-list">
        {visibleNeighborhoods.map((item) => <button key={`${item.city}:${item.neighborhood}`} onClick={() => onNeighborhood(item)} title={`Filtrar pedidos de ${item.neighborhood}`}>
          <span className="orders-neighborhood-rank">{selected.neighborhoods.indexOf(item) + 1}</span>
          <span className="orders-neighborhood-name"><strong>{item.neighborhood}</strong><small>{item.city}</small></span>
          <span className="orders-neighborhood-bar"><i style={{ width: `${Math.max(3, (item.total / largest) * 100)}%` }} /></span>
          <span className="orders-neighborhood-value"><strong>{item.total.toLocaleString('pt-BR')}</strong><small>{item.pending} pend.</small></span>
        </button>)}
        {!selected.neighborhoods.length ? <div className="orders-neighborhood-empty">Nenhum pedido nesta região.</div> : null}
      </div>
      {selected.neighborhoods.length > 8 ? <button className="orders-neighborhood-more" onClick={onToggleExpanded}>{expanded ? 'Mostrar resumo' : `Ver todos os ${selected.neighborhoods.length} bairros`} <ChevronDown className={expanded ? 'expanded' : ''} size={14} /></button> : null}
    </div>
  </section>
}

export function OrdersPage() {
  const [scope, setScope] = useState<'active' | 'trash'>('active')
  const [orders, setOrders] = useState<Order[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [locations, setLocations] = useState<OrderLocationState[]>([])
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const [exportFields, setExportFields] = useState<OrderExportFieldId[]>(DEFAULT_ORDER_EXPORT_FIELDS)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState('')
  const [message, setMessage] = useState('')
  const [importError, setImportError] = useState('')
  const [reviews, setReviews] = useState<DuplicateReview[]>([])
  const [reviewOpen, setReviewOpen] = useState(false)
  const [geocoding, setGeocoding] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncState, setSyncState] = useState<SyncStatus | null>(null)
  const [imports, setImports] = useState<ImportRun[]>([])
  const [importsLoading, setImportsLoading] = useState(true)
  const [importsError, setImportsError] = useState('')
  const [importHistoryOpen, setImportHistoryOpen] = useState(false)
  const [importFilter, setImportFilter] = useState<ImportRun | null>(null)
  const [detail, setDetail] = useState<OrderDetail | null>(null)
  const [attempts, setAttempts] = useState<DeliveryAttempt[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [editingDetail, setEditingDetail] = useState(false)
  const [editForm, setEditForm] = useState<OrderEditForm | null>(null)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [cepLookingUp, setCepLookingUp] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [rollbackRun, setRollbackRun] = useState<ImportRun | null>(null)
  const [rollbackPreview, setRollbackPreview] = useState<ImportRollbackPreview | null>(null)
  const [rollbackReason, setRollbackReason] = useState('')
  const [rollbackLoading, setRollbackLoading] = useState(false)
  const [overview, setOverview] = useState<OrdersOverview | null>(null)
  const [overviewRefreshing, setOverviewRefreshing] = useState(false)
  const [selectedRegion, setSelectedRegion] = useState('')
  const [expandedRegion, setExpandedRegion] = useState(false)
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('today')
  const [periodRange, setPeriodRange] = useState<PeriodRange>(() => rangeForPreset('today'))
  const [customFrom, setCustomFrom] = useState(() => rangeForPreset('thirtyDays').from)
  const [customTo, setCustomTo] = useState(() => rangeForPreset('today').to)
  const fileRef = useRef<HTMLInputElement>(null)

  const loadOverview = useCallback(async (quiet = false) => {
    if (!quiet) setOverviewRefreshing(true)
    try {
      const params = new URLSearchParams(periodRange)
      const result = await api<OrdersOverview>(`/api/orders/overview?${params}`)
      setOverview(result)
      setSelectedRegion((current) => current && result.regions.some((region) => region.key === current) ? current : result.regions.find((region) => region.total > 0)?.key ?? result.regions[0]?.key ?? '')
    } finally { if (!quiet) setOverviewRefreshing(false) }
  }, [periodRange])

  const loadImportHistory = useCallback(async () => {
    setImportsLoading(true)
    setImportsError('')
    try {
      const result = await api<{ imports: ImportRun[] }>('/api/imports?kind=manual&limit=50')
      setImports(result.imports)
    } catch (caught) {
      setImportsError(caught instanceof ApiError ? caught.message : 'Não foi possível carregar o histórico.')
    } finally { setImportsLoading(false) }
  }, [])

  const loadLocations = useCallback(async () => {
    try {
      const result = await api<{ states: OrderLocationState[] }>(`/api/orders/locations?scope=${scope}`)
      setLocations(result.states)
    } catch {
      // Os filtros geográficos são auxiliares; a lista continua disponível.
    }
  }, [scope])

  const choosePeriod = (preset: PeriodPreset) => {
    setPeriodPreset(preset)
    if (preset !== 'custom') setPeriodRange(rangeForPreset(preset))
  }

  const load = useCallback(async () => {
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    params.set('scope', scope)
    if (status) params.set('status', status)
    if (search) params.set('search', search)
    if (stateFilter) params.set('state', stateFilter)
    if (cityFilter) params.set('city', cityFilter)
    if (importFilter) params.set('importRunId', importFilter.id)
    try {
      const result = await api<{ orders: Order[]; total: number; page: number; pageSize: number }>(`/api/orders?${params}`)
      setOrders(result.orders)
      setTotal(result.total)
      const lastPage = Math.max(1, Math.ceil(result.total / pageSize))
      if (page > lastPage) setPage(lastPage)
      const duplicateResult = await api<{ reviews: DuplicateReview[] }>('/api/duplicates')
      setReviews(duplicateResult.reviews)
      try {
        setSyncState(await api<SyncStatus>('/api/imports/sync'))
      } catch {
        // O estado da sincronização é informativo: sua falha não derruba a lista.
      }
    } finally { setLoading(false) }
  }, [cityFilter, importFilter, page, pageSize, scope, search, stateFilter, status])

  useEffect(() => { const timer = window.setTimeout(() => void load(), 250); return () => window.clearTimeout(timer) }, [load])
  useEffect(() => { void loadImportHistory() }, [loadImportHistory])
  useEffect(() => { void loadLocations() }, [loadLocations])
  useEffect(() => {
    void loadOverview()
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void loadOverview(true) }, 30_000)
    return () => window.clearInterval(timer)
  }, [loadOverview])

  const importFile = async () => {
    const file = fileRef.current?.files?.[0]
    if (!file) return
    setImporting(true)
    setMessage('')
    setImportError('')
    try {
      const parsed = await parseImportFile(file)
      const checksum = bytesToBase64Url(await crypto.subtle.digest('SHA-256', parsed.bytes))
      let rows = parsed.rows.map((row) => row.externalId ? row : {
        ...row,
        externalId: `manual:${checksum}:${row.sourceRowNumber}`,
      })
      const pendingCeps = [...new Set(rows
        .filter((row) => row.needsAddressReview && row.neighborhood === 'A classificar')
        .map((row) => row.cep.replace(/\D/g, ''))
        .filter((cep) => cep.length === 8))]
      if (pendingCeps.length) {
        const addresses: Record<string, { cep: string; street: string; complement: string; neighborhood: string; city: string; state: string }> = {}
        for (let index = 0; index < pendingCeps.length; index += 30) {
          setProgress(`Identificando bairros por CEP · ${Math.min(index + 30, pendingCeps.length)} de ${pendingCeps.length}`)
          const result = await api<{ addresses: typeof addresses }>('/api/address/ceps', {
            method: 'POST', body: JSON.stringify({ ceps: pendingCeps.slice(index, index + 30) }),
          })
          Object.assign(addresses, result.addresses)
        }
        rows = rows.map((row) => {
          const resolved = addresses[row.cep.replace(/\D/g, '')]
          if (!resolved?.neighborhood) return row
          return {
            ...row,
            cep: resolved.cep,
            street: row.street || resolved.street,
            neighborhood: resolved.neighborhood,
            city: resolved.city || row.city,
            state: resolved.state || row.state,
          }
        })
      }
      const invalid = rows
        .map((row) => ({ row: row.sourceRowNumber, errors: validateImportedRow(row) }))
        .filter((item) => item.errors.length)
      if (invalid.length) {
        const sample = invalid.slice(0, 5).map((item) => `linha ${item.row}: ${item.errors.join(', ')}`).join('; ')
        throw new Error(`${invalid.length} linha(s) precisam de correção antes da importação. ${sample}`)
      }
      const started = await api<{ id: string; alreadyImported: boolean }>('/api/imports/start', {
        method: 'POST', body: JSON.stringify({ checksum, rowCount: rows.length, sourceType: 'CSV_UPLOAD', sourceRef: file.name }),
      })
      if (started.alreadyImported) {
        setMessage('Esta cópia da planilha já foi importada. Nenhum pedido foi duplicado.')
      } else {
        let errors = 0
        for (let index = 0; index < rows.length; index += 100) {
          const chunk = rows.slice(index, index + 100)
          setProgress(`Processando ${Math.min(index + chunk.length, rows.length)} de ${rows.length}`)
          const result = await api<{ errors: unknown[] }>(`/api/imports/${started.id}/chunk`, { method: 'POST', body: JSON.stringify({ rows: chunk }) })
          errors += result.errors.length
        }
        const finished = await api<{ processed: number; expected: number }>(`/api/imports/${started.id}/finish`, { method: 'POST' })
        let addressesAnalyzed = 0
        for (let batch = 0; batch < Math.ceil(rows.length / 50) + 1; batch += 1) {
          setProgress(`Identificando bairros dos endereços · ${addressesAnalyzed.toLocaleString('pt-BR')} analisados`)
          const geocodeResult = await api<{ processed: number }>('/api/geocoding/run', {
            method: 'POST', body: JSON.stringify({ limit: 50 }),
          })
          addressesAnalyzed += geocodeResult.processed
          if (geocodeResult.processed === 0) break
        }
        const reviewMessage = parsed.reviewCount ? `; ${parsed.reviewCount.toLocaleString('pt-BR')} endereços livres entraram para conferência` : ''
        const geocodeMessage = addressesAnalyzed ? `; ${addressesAnalyzed.toLocaleString('pt-BR')} endereços analisados automaticamente` : ''
        setMessage(`${finished.processed.toLocaleString('pt-BR')} linhas processadas${errors ? `; ${errors} precisam de correção` : ''}${reviewMessage}${geocodeMessage}.`)
      }
      setImportOpen(false)
      await Promise.all([load(), loadOverview(), loadImportHistory()])
    } catch (caught) {
      setImportError(caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : 'Falha ao importar')
      await loadImportHistory()
    } finally { setImporting(false); setProgress('') }
  }

  const openDetail = async (orderId: string) => {
    setDetailLoading(true)
    setEditingDetail(false)
    setEditForm(null)
    setEditError('')
    try {
      const result = await api<{ order: OrderDetail; attempts: DeliveryAttempt[] }>(`/api/orders/${orderId}`)
      setDetail(result.order)
      setAttempts(result.attempts)
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Falha ao abrir o pedido')
    } finally { setDetailLoading(false) }
  }

  const startEditingDetail = () => {
    if (!detail) return
    setEditForm(editFormFromOrder(detail))
    setEditError('')
    setEditingDetail(true)
  }

  const updateEditField = (field: keyof OrderEditForm, value: string) => {
    setEditForm((current) => current ? { ...current, [field]: value } : current)
  }

  const lookupCep = async () => {
    if (!editForm) return
    const cep = editForm.cep.replace(/\D/g, '')
    if (cep.length !== 8) { setEditError('Informe os 8 números do CEP.'); return }
    setCepLookingUp(true)
    setEditError('')
    try {
      const result = await api<{ address: { cep: string; street: string; complement: string; neighborhood: string; city: string; state: string } }>(`/api/address/cep/${cep}`)
      setEditForm((current) => current ? {
        ...current,
        cep: result.address.cep,
        street: result.address.street || current.street,
        complement: current.complement || result.address.complement,
        neighborhood: result.address.neighborhood || current.neighborhood,
        city: result.address.city || current.city,
        state: result.address.state || current.state,
      } : current)
    } catch (caught) {
      setEditError(caught instanceof ApiError ? caught.message : 'Não foi possível consultar o CEP.')
    } finally { setCepLookingUp(false) }
  }

  const saveOrderEdit = async () => {
    if (!detail || !editForm) return
    const models = editForm.stickerModels.split(/[,+|;]/).map((item) => item.trim()).filter(Boolean)
    setEditSaving(true)
    setEditError('')
    try {
      const result = await api<{ status: string; pendingDuplicateReview: boolean }>(`/api/orders/${detail.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...editForm,
          quantity: Number(editForm.quantity),
          stickerModels: models,
          markReady: detail.status === 'NEEDS_REVIEW',
        }),
      })
      const refreshed = await api<{ order: OrderDetail; attempts: DeliveryAttempt[] }>(`/api/orders/${detail.id}`)
      setDetail(refreshed.order)
      setAttempts(refreshed.attempts)
      setEditingDetail(false)
      setEditForm(null)
      setMessage(result.pendingDuplicateReview ? 'Correções salvas. A duplicidade ainda precisa ser resolvida.' : 'Pedido atualizado com sucesso.')
      await Promise.all([load(), loadOverview(), loadLocations()])
    } catch (caught) {
      setEditError(caught instanceof ApiError ? caught.message : 'Não foi possível salvar o pedido.')
    } finally { setEditSaving(false) }
  }

  const deleteOrder = async () => {
    if (!detail || deleteReason.trim().length < 3) return
    setDeleting(true)
    try {
      await api(`/api/orders/${detail.id}`, { method: 'DELETE', body: JSON.stringify({ reason: deleteReason.trim() }) })
      setDeleteOpen(false)
      setDeleteReason('')
      setDetail(null)
      setMessage('Pedido movido para Cancelados e excluídos. Ele pode ser restaurado quando necessário.')
      await Promise.all([load(), loadOverview(), loadLocations()])
    } catch (caught) {
      setEditError(caught instanceof ApiError ? caught.message : 'Não foi possível excluir o pedido da operação.')
      setDeleteOpen(false)
    } finally { setDeleting(false) }
  }

  const restoreOrder = async () => {
    if (!detail) return
    setDeleting(true)
    try {
      await api(`/api/orders/${detail.id}/restore`, { method: 'POST' })
      setDetail(null)
      setMessage('Pedido restaurado e devolvido para a operação.')
      await Promise.all([load(), loadOverview(), loadLocations()])
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Não foi possível restaurar o pedido.')
    } finally { setDeleting(false) }
  }

  const openRollback = async (run: ImportRun) => {
    setRollbackRun(run)
    setRollbackPreview(null)
    setRollbackReason('')
    setRollbackLoading(true)
    try {
      const result = await api<{ preview: ImportRollbackPreview }>(`/api/imports/${run.id}/rollback-preview`)
      setRollbackPreview(result.preview)
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Não foi possível analisar esta importação.')
      setRollbackRun(null)
    } finally { setRollbackLoading(false) }
  }

  const rollbackImport = async () => {
    if (!rollbackRun || !rollbackPreview || rollbackReason.trim().length < 3) return
    setRollbackLoading(true)
    try {
      const result = await api<{ cancelled: number; protectedOrders: number; mergedOrders: number }>(`/api/imports/${rollbackRun.id}/rollback`, {
        method: 'POST', body: JSON.stringify({ reason: rollbackReason.trim() }),
      })
      setRollbackRun(null)
      setRollbackPreview(null)
      setRollbackReason('')
      setMessage(`Importação desfeita: ${result.cancelled.toLocaleString('pt-BR')} pedidos foram para Cancelados e excluídos. ${result.protectedOrders.toLocaleString('pt-BR')} protegidos e ${result.mergedOrders.toLocaleString('pt-BR')} mesclados foram preservados.`)
      await Promise.all([load(), loadOverview(), loadLocations(), loadImportHistory()])
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Não foi possível desfazer a importação.')
    } finally { setRollbackLoading(false) }
  }

  const geocode = async () => {
    setGeocoding(true)
    setMessage('')
    try {
      let processedTotal = 0
      let failures = 0
      for (let batch = 0; batch < 40; batch += 1) {
        const result = await api<{ processed: number; geocoded: number; failed: number }>('/api/geocoding/run', { method: 'POST', body: JSON.stringify({ limit: 50 }) })
        processedTotal += result.processed
        failures += result.failed
        setProgress(`${processedTotal.toLocaleString('pt-BR')} endereços analisados`)
        if (result.processed === 0) break
        await new Promise((resolve) => window.setTimeout(resolve, 250))
      }
      setMessage(`${processedTotal.toLocaleString('pt-BR')} endereços processados${failures ? `; ${failures} precisam de revisão manual` : ''}.`)
      await Promise.all([load(), loadOverview()])
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Falha ao localizar endereços')
    } finally { setGeocoding(false); setProgress('') }
  }

  const syncNow = async () => {
    setSyncing(true)
    setMessage('')
    try {
      let inserted = 0
      let invalid = 0
      let remaining = 0
      for (let round = 0; round < 20; round += 1) {
        const result = await api<{ inserted: number; merged: number; invalid: number; remaining: number }>(
          '/api/imports/sync', { method: 'POST' },
        )
        inserted += result.inserted + result.merged
        invalid = result.invalid
        remaining = result.remaining
        setProgress(remaining ? `${remaining.toLocaleString('pt-BR')} pedidos ainda na fila` : 'Concluindo sincronização')
        if (!remaining) break
      }
      const parts = [`${inserted.toLocaleString('pt-BR')} pedidos novos`]
      if (invalid) parts.push(`${invalid} aguardam correção de endereço`)
      if (remaining) parts.push(`${remaining} continuam na fila para a próxima rodada`)
      setMessage(`${parts.join('; ')}.`)
      await Promise.all([load(), loadOverview()])
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Falha ao sincronizar os pedidos')
    } finally { setSyncing(false); setProgress('') }
  }

  const resolveReview = async (id: string) => {
    await api(`/api/duplicates/${id}/resolve`, { method: 'POST', body: JSON.stringify({ resolution: 'KEPT_SEPARATE' }) })
    await load()
  }

  const toggleExportField = (field: OrderExportFieldId) => {
    setExportFields((current) => current.includes(field) ? current.filter((item) => item !== field) : [...current, field])
  }

  const viewImportedOrders = (run: ImportRun, reviewOnly: boolean) => {
    setImportFilter(run)
    setSearch('')
    setStatus(reviewOnly ? 'NEEDS_REVIEW' : '')
    setStateFilter('')
    setCityFilter('')
    setPage(1)
    setImportHistoryOpen(false)
    window.setTimeout(() => document.querySelector('.toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
  }

  const goToPage = (nextPage: number) => {
    setPage(nextPage)
    window.setTimeout(() => document.querySelector('.table-meta')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
  }

  const exportOrders = async () => {
    if (!exportFields.length) return
    setExporting(true)
    setExportError('')
    try {
      const result = await apiFile('/api/orders/export', {
        method: 'POST',
        body: JSON.stringify({
          fields: exportFields, status: status || undefined, search: search.trim() || undefined,
          state: stateFilter || undefined, city: cityFilter || undefined, importRunId: importFilter?.id, scope,
        }),
      })
      const url = URL.createObjectURL(result.blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = result.filename
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
      setExportOpen(false)
      setMessage(`${total.toLocaleString('pt-BR')} pedidos preparados para exportação.`)
    } catch (caught) {
      setExportError(caught instanceof ApiError ? caught.message : 'Não foi possível exportar os pedidos')
    } finally { setExporting(false) }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const firstVisible = total ? (page - 1) * pageSize + 1 : 0
  const lastVisible = total ? Math.min(page * pageSize, total) : 0
  const availableCities = locations.find((item) => item.state === stateFilter)?.cities ?? []

  return (
    <div className="page">
      <header className="page-header"><div><p className="eyebrow">Base de entrega</p><h1>{scope === 'trash' ? 'Cancelados e excluídos' : 'Pedidos'}</h1><p>{scope === 'trash' ? 'Itens retirados da operação permanecem preservados e podem ser restaurados.' : 'Importe os pedidos da Tainá e acompanhe cada etapa da entrega.'}</p></div><div className="header-actions"><Button variant="secondary" onClick={() => { setExportError(''); setExportOpen(true) }} disabled={!total}><Download size={16} /> Exportar</Button>{scope === 'active' ? <><Button variant="secondary" onClick={() => void geocode()} loading={geocoding}><LocateFixed size={16} /> Localizar endereços</Button><Button variant="secondary" onClick={() => setImportOpen(true)}><FileUp size={16} /> Importar planilha</Button>{syncState?.configured ? <Button onClick={() => void syncNow()} loading={syncing}><RefreshCw size={16} /> Sincronizar agora</Button> : null}</> : null}</div></header>
      <nav className="orders-lifecycle-tabs" aria-label="Área dos pedidos">
        <button type="button" className={scope === 'active' ? 'active' : ''} onClick={() => { setScope('active'); setStatus(''); setImportFilter(null); setPage(1) }}><ListFilter size={15} /> Pedidos ativos</button>
        <button type="button" className={scope === 'trash' ? 'active' : ''} onClick={() => { setScope('trash'); setStatus(''); setImportFilter(null); setPage(1) }}><Trash2 size={15} /> Cancelados e excluídos</button>
      </nav>
      {scope === 'active' ? <OverviewPanel overview={overview} selectedKey={selectedRegion} expanded={expandedRegion} refreshing={overviewRefreshing} periodPreset={periodPreset} periodRange={periodRange} customFrom={customFrom} customTo={customTo} onSelectRegion={(key) => { setSelectedRegion(key); setExpandedRegion(false) }} onToggleExpanded={() => setExpandedRegion((value) => !value)} onRefresh={() => void loadOverview()} onNeighborhood={(item) => { setPage(1); setStateFilter(''); setCityFilter(''); setSearch(item.city === 'Rio de Janeiro' ? item.neighborhood : `${item.neighborhood} ${item.city}`); window.setTimeout(() => document.querySelector('.toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50) }} onPreset={choosePeriod} onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} onApplyCustom={() => { if (customFrom && customTo && customFrom <= customTo) setPeriodRange({ from: customFrom, to: customTo }) }} /> : null}
      {scope === 'active' ? <ImportHistoryPanel imports={imports} loading={importsLoading} error={importsError} onView={viewImportedOrders} onRollback={(run) => void openRollback(run)} onOpenAll={() => setImportHistoryOpen(true)} /> : null}
      {scope === 'active' ? <SyncBanner state={syncState} /> : null}
      {message ? <div className="notice">{message}</div> : null}
      {progress ? <div className="progress-note"><RefreshCw size={15} className="spin" />{progress}</div> : null}
      <section className="toolbar orders-toolbar">
        <div className="search-field"><Search size={16} /><input aria-label="Buscar pedidos por nome, telefone, bairro ou CEP" placeholder="Buscar nome, telefone, bairro ou CEP" value={search} onChange={(event) => { setPage(1); setSearch(event.target.value) }} /></div>
        <select aria-label="Filtrar por estado" value={stateFilter} onChange={(event) => { setPage(1); setStateFilter(event.target.value); setCityFilter('') }}>
          <option value="">Todos os estados</option>{locations.map((item) => <option value={item.state} key={item.state}>{item.state} · {STATE_LABELS[item.state] ?? item.state} ({item.total.toLocaleString('pt-BR')})</option>)}
        </select>
        <select aria-label="Filtrar por cidade" value={cityFilter} disabled={!stateFilter} onChange={(event) => { setPage(1); setCityFilter(event.target.value) }}>
          <option value="">{stateFilter ? 'Todas as cidades' : 'Escolha o estado'}</option>{availableCities.map((item) => <option value={item.city} key={item.city}>{item.city} ({item.total.toLocaleString('pt-BR')})</option>)}
        </select>
        <select aria-label="Filtrar por situação" value={status} onChange={(event) => { setPage(1); setStatus(event.target.value) }}>
          <option value="">{scope === 'trash' ? 'Todos os cancelados' : 'Todos os pedidos'}</option>{scope === 'active' ? <><option value="READY">Prontos</option><option value="NEEDS_REVIEW">Revisar</option><option value="NOT_FOUND">Nova tentativa</option><option value="DELIVERED">Entregues</option></> : <option value="CANCELLED">Cancelados</option>}
        </select>
        {reviews.length ? <Button variant="secondary" onClick={() => setReviewOpen(true)}>{reviews.length} duplicidades</Button> : null}
      </section>
      {importFilter ? <div className="import-active-filter"><History size={14} /><span>Exibindo somente a importação: <strong>{importFilter.sourceRef || 'Importação manual'}</strong></span>{status === 'NEEDS_REVIEW' ? <small>e somente os que ainda precisam de revisão</small> : null}<button type="button" onClick={() => { setPage(1); setImportFilter(null); setStatus('') }} aria-label="Remover filtro de importação"><X size={14} /> Ver todos os pedidos</button></div> : null}
      <div className="table-meta"><span>{total.toLocaleString('pt-BR')} {total === 1 ? 'pedido' : 'pedidos'}</span><small>{firstVisible.toLocaleString('pt-BR')}–{lastVisible.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} · Página {page.toLocaleString('pt-BR')} de {totalPages.toLocaleString('pt-BR')}</small></div>
      <div className="data-table-wrap">
        <table className="data-table"><thead><tr><th>Destinatário</th><th>Endereço</th><th>Material</th><th>Localização</th><th>Situação</th></tr></thead>
          <tbody>{orders.map((order) => <tr key={order.id}><td><button className="table-link" onClick={() => void openDetail(order.id)} disabled={detailLoading}><strong>{order.recipientName}</strong><small>{order.phone}</small></button></td><td><span>{order.street}, {order.streetNumber}</span><small>{order.neighborhood} · {order.city} · {order.cep ? `CEP ${order.cep}` : 'CEP não informado'}</small></td><td><span>{(JSON.parse(order.stickerModelsJson || '[]') as string[]).join(', ') || 'Não informado'}</span><small>{order.quantity} unidade(s) · {sourceLabel(order.source)}</small></td><td><Status value={order.geocodeStatus} /></td><td><Status value={order.status} />{order.deletedAt ? <small className="trash-row-note">Retirado em {formatMoment(order.deletedAt)}</small> : null}</td></tr>)}</tbody>
        </table>
        {!loading && !orders.length ? <EmptyState title={scope === 'trash' ? 'Nenhum pedido cancelado ou excluído' : 'Nenhum pedido encontrado'} description={scope === 'trash' ? 'Quando um pedido for retirado da operação, ele aparecerá aqui sem perder o histórico.' : 'Altere o filtro ou importe a cópia mais recente da planilha.'} /> : null}
      </div>
      <nav className="orders-pagination" aria-label="Paginação dos pedidos">
        <label><span>Por página</span><select aria-label="Pedidos por página" value={pageSize} onChange={(event) => { setPage(1); setPageSize(Number(event.target.value)) }}><option value="25">25</option><option value="50">50</option><option value="100">100</option></select></label>
        <div className="orders-pagination-buttons">
          <button type="button" aria-label="Página anterior" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)}><ChevronLeft size={14} /><span>Anterior</span></button>
          <div className="orders-page-numbers">{paginationPages(page, totalPages).map((number) => <button type="button" key={number} aria-label={`Página ${number}`} aria-current={number === page ? 'page' : undefined} className={number === page ? 'active' : ''} disabled={loading} onClick={() => goToPage(number)}>{number}</button>)}</div>
          <button type="button" aria-label="Próxima página" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)}><span>Próxima</span><ChevronRight size={14} /></button>
        </div>
      </nav>
      {exportOpen ? <Modal title="Exportar pedidos" onClose={() => !exporting && setExportOpen(false)} size="wide"><div className="modal-body export-builder">
        <section className="export-summary"><FileSpreadsheet size={21} /><div><strong>{total.toLocaleString('pt-BR')} pedidos no arquivo</strong><span>{search || status || stateFilter || cityFilter || importFilter ? 'Todos os filtros selecionados serão respeitados, não apenas a página visível.' : 'Toda a base de pedidos será exportada.'}</span></div><small>CSV · Google Sheets e Excel</small></section>
        <div className="export-presets"><span>Seleção rápida</span><div>{ORDER_EXPORT_PRESETS.map((preset) => <button type="button" key={preset.label} onClick={() => setExportFields([...preset.fields])}>{preset.label}</button>)}<button type="button" onClick={() => setExportFields([])}>Limpar</button></div></div>
        <div className="export-fields-heading"><div><strong>Escolha as informações</strong><span>Marque somente as colunas que devem aparecer no arquivo.</span></div><small>{exportFields.length} de {ORDER_EXPORT_FIELDS.length} selecionadas</small></div>
        <div className="export-field-groups">{[...new Set(ORDER_EXPORT_FIELDS.map((field) => field.group))].map((group) => <fieldset key={group}><legend>{group}</legend>{ORDER_EXPORT_FIELDS.filter((field) => field.group === group).map((field) => <label key={field.id} className={exportFields.includes(field.id) ? 'selected' : ''}><input type="checkbox" checked={exportFields.includes(field.id)} onChange={() => toggleExportField(field.id)} /><span>{field.label}</span></label>)}</fieldset>)}</div>
        <p className="export-privacy-note">O arquivo contém dados pessoais. A geração ficará registrada na auditoria do sistema.</p>
        {exportError ? <div className="form-alert export-alert">{exportError}</div> : null}
      </div><footer className="modal-footer"><Button variant="ghost" onClick={() => setExportOpen(false)} disabled={exporting}>Cancelar</Button><Button onClick={() => void exportOrders()} loading={exporting} disabled={!exportFields.length}><Download size={16} /> Baixar {exportFields.length} colunas</Button></footer></Modal> : null}
      {importOpen ? <Modal title="Importar pedidos" onClose={() => !importing && setImportOpen(false)}><div className="modal-body"><p className="modal-intro">Envie CSV, XLSX ou XLS. O sistema reconhece a planilha oficial e os formatos de redes sociais e call center, mesmo quando o cabeçalho começa depois das primeiras linhas.</p><label className="file-drop"><FileUp size={24} /><strong>Selecionar planilha</strong><span>CSV, XLSX ou XLS · até 15 MB</span><input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={() => setImportError('')} /></label><p className="modal-intro import-review-note">Endereços escritos em uma única coluna entram marcados para conferência, sem serem descartados.</p>{importError ? <div className="form-alert import-alert">{importError}</div> : null}</div><footer className="modal-footer"><Button variant="ghost" onClick={() => setImportOpen(false)} disabled={importing}>Cancelar</Button><Button onClick={() => void importFile()} loading={importing}>Importar cópia</Button></footer></Modal> : null}
      {importHistoryOpen ? <Modal title="Histórico de importações" onClose={() => setImportHistoryOpen(false)} size="wide"><div className="modal-body import-history-modal"><p className="modal-intro">Cada envio permanece registrado com o arquivo, horário, responsável e resultado do processamento.</p><ImportHistoryRows imports={imports} onView={viewImportedOrders} onRollback={(run) => void openRollback(run)} /></div></Modal> : null}
      {rollbackRun ? <Modal title="Desfazer importação" onClose={() => !rollbackLoading && setRollbackRun(null)}><div className="modal-body rollback-dialog">
        <div className="rollback-file"><Undo2 size={18} /><div><strong>{rollbackRun.sourceRef || 'Importação manual'}</strong><span>{formatMoment(rollbackRun.completedAt ?? rollbackRun.createdAt)}</span></div></div>
        {rollbackLoading && !rollbackPreview ? <p className="modal-intro">Analisando os vínculos desta importação…</p> : null}
        {rollbackPreview ? <><div className="rollback-impact"><article><strong>{rollbackPreview.canCancel.toLocaleString('pt-BR')}</strong><span>serão retirados</span></article><article><strong>{rollbackPreview.protectedOrders.toLocaleString('pt-BR')}</strong><span>protegidos</span></article><article><strong>{rollbackPreview.mergedOrders.toLocaleString('pt-BR')}</strong><span>mesclados preservados</span></article></div><p className="modal-intro">Somente pedidos criados exclusivamente por este arquivo e ainda sem uso operacional serão movidos para Cancelados e excluídos. Nada será apagado definitivamente.</p><label className="field"><span>Motivo</span><textarea rows={3} value={rollbackReason} onChange={(event) => setRollbackReason(event.target.value)} placeholder="Ex.: arquivo importado duas vezes" /></label></> : null}
      </div><footer className="modal-footer"><Button variant="ghost" disabled={rollbackLoading} onClick={() => setRollbackRun(null)}>Cancelar</Button><Button variant="danger" loading={rollbackLoading} disabled={!rollbackPreview || rollbackReason.trim().length < 3} onClick={() => void rollbackImport()}><Undo2 size={15} /> Desfazer com segurança</Button></footer></Modal> : null}
      {deleteOpen && detail ? <Modal title="Excluir pedido da operação" onClose={() => !deleting && setDeleteOpen(false)}><div className="modal-body rollback-dialog"><div className="delete-order-warning"><Trash2 size={18} /><div><strong>{detail.recipientName}</strong><span>O pedido sairá das listas operacionais, mas continuará preservado.</span></div></div><label className="field"><span>Motivo da exclusão</span><textarea autoFocus rows={3} value={deleteReason} onChange={(event) => setDeleteReason(event.target.value)} placeholder="Informe por que este pedido está sendo retirado" /></label></div><footer className="modal-footer"><Button variant="ghost" disabled={deleting} onClick={() => setDeleteOpen(false)}>Voltar</Button><Button variant="danger" loading={deleting} disabled={deleteReason.trim().length < 3} onClick={() => void deleteOrder()}><Trash2 size={15} /> Mover para excluídos</Button></footer></Modal> : null}
      {reviewOpen ? <Modal title="Revisar possíveis duplicidades" onClose={() => setReviewOpen(false)}><div className="modal-body review-list">{reviews.map((review) => <article key={review.id}><div><strong>{review.reviewType === 'SAME_PHONE_DIFFERENT_ADDRESS' ? 'Mesmo telefone, endereços diferentes' : 'Mesmo endereço, telefones diferentes'}</strong><p>{review.primaryName} · {review.primaryPhone}</p><small>{review.primaryAddress}</small><p>{review.relatedName} · {review.relatedPhone}</p><small>{review.relatedAddress}</small></div><Button variant="secondary" onClick={() => void resolveReview(review.id)}>Manter separados</Button></article>)}</div></Modal> : null}
      {detail && !deleteOpen ? <Modal title={editingDetail ? 'Corrigir pedido' : 'Detalhes do pedido'} size={editingDetail ? 'wide' : 'default'} onClose={() => { if (editSaving) return; setDetail(null); setAttempts([]); setEditingDetail(false); setEditForm(null); setEditError('') }}>
        {editingDetail && editForm ? <>
          <div className="modal-body order-edit-form">
            <div className="order-edit-note"><Pencil size={15} /><span>Altere somente o que estiver incorreto. Ao trocar o endereço, ele será localizado novamente antes de entrar em rota.</span></div>
            <div className="order-edit-grid">
              <label className="field"><span>Nome do destinatário</span><input autoFocus value={editForm.recipientName} onChange={(event) => updateEditField('recipientName', event.target.value)} /></label>
              <label className="field"><span>WhatsApp com DDD</span><input inputMode="tel" value={editForm.phone} onChange={(event) => updateEditField('phone', event.target.value)} /></label>
              <div className="order-edit-cep"><label className="field"><span>CEP</span><input inputMode="numeric" maxLength={9} value={editForm.cep} onChange={(event) => updateEditField('cep', event.target.value)} /></label><Button variant="secondary" loading={cepLookingUp} onClick={() => void lookupCep()}><Search size={14} /> Buscar CEP</Button></div>
              <label className="field"><span>Rua</span><input value={editForm.street} onChange={(event) => updateEditField('street', event.target.value)} /></label>
              <label className="field"><span>Número</span><input value={editForm.streetNumber} onChange={(event) => updateEditField('streetNumber', event.target.value)} /></label>
              <label className="field"><span>Complemento</span><input value={editForm.complement} onChange={(event) => updateEditField('complement', event.target.value)} /></label>
              <label className="field"><span>Bairro</span><input value={editForm.neighborhood} onChange={(event) => updateEditField('neighborhood', event.target.value)} /></label>
              <label className="field"><span>Cidade</span><input value={editForm.city} onChange={(event) => updateEditField('city', event.target.value)} /></label>
              <label className="field"><span>Estado</span><input maxLength={2} value={editForm.state} onChange={(event) => updateEditField('state', event.target.value.toUpperCase())} /></label>
              <label className="field full-span"><span>Ponto de referência</span><input value={editForm.referencePoint} onChange={(event) => updateEditField('referencePoint', event.target.value)} /></label>
              <label className="field"><span>Quantidade</span><input type="number" min={1} max={10000} value={editForm.quantity} onChange={(event) => updateEditField('quantity', event.target.value)} /></label>
              <label className="field"><span>Material</span><input value={editForm.stickerModels} onChange={(event) => updateEditField('stickerModels', event.target.value)} /></label>
            </div>
            {detail.status === 'NEEDS_REVIEW' ? <p className="order-edit-ready-note"><CheckCheck size={14} /> Com o endereço completo, o pedido será marcado como pronto automaticamente.</p> : null}
            {editError ? <div className="form-alert order-edit-alert">{editError}</div> : null}
          </div>
          <footer className="modal-footer"><Button variant="ghost" disabled={editSaving} onClick={() => { setEditingDetail(false); setEditForm(null); setEditError('') }}>Cancelar</Button><Button loading={editSaving} onClick={() => void saveOrderEdit()} disabled={!editForm.recipientName.trim() || !editForm.phone.trim() || ![0, 8].includes(editForm.cep.replace(/\D/g, '').length) || !editForm.street.trim() || !editForm.streetNumber.trim() || !editForm.neighborhood.trim() || !editForm.city.trim() || editForm.state.trim().length !== 2 || Number(editForm.quantity) < 1}><Save size={15} /> Salvar correção</Button></footer>
        </> : <><div className="modal-body order-detail">
          <section><div className="order-detail-heading"><span className="detail-label">Destinatário</span>{!['DELIVERED', 'CANCELLED'].includes(detail.status) ? <button type="button" className="order-edit-trigger" onClick={startEditingDetail}><Pencil size={13} /> Editar</button> : null}</div><h3>{detail.recipientName}</h3><a href={`tel:+${detail.phone.replace(/\D/g, '')}`}>{detail.phone}</a></section>
          <section><span className="detail-label">Endereço completo</span><p>{detail.street}, {detail.streetNumber}{detail.complement ? ` · ${detail.complement}` : ''}</p><p>{detail.neighborhood} · {detail.city} - {detail.state} · {detail.cep ? `CEP ${detail.cep}` : 'CEP não informado'}</p>{detail.referencePoint ? <small>Referência: {detail.referencePoint}</small> : null}</section>
          <section><span className="detail-label">Material</span><p>{stickerModels(detail.stickerModelsJson).join(' + ') || 'Não informado'} · {detail.quantity} unidade(s)</p><small>Origem: {sourceLabel(detail.source)}</small><div className="detail-statuses"><Status value={detail.status} /><Status value={detail.geocodeStatus} /></div></section>
          {detail.deletedAt || detail.status === 'CANCELLED' ? <section className="order-trash-detail"><span className="detail-label">Cancelamento / exclusão</span><p>{detail.deletionReason || 'Motivo não registrado'}</p><small>{detail.deletedAt ? `Retirado em ${formatMoment(detail.deletedAt)}` : 'Cancelado anteriormente'}{detail.deletedByName ? ` · por ${detail.deletedByName}` : ''}</small></section> : null}
          <section><span className="detail-label">Histórico de entrega</span>{attempts.length ? <div className="attempt-list">{attempts.map((attempt) => <article key={attempt.id}><div><strong>{attempt.outcome === 'DELIVERED' ? 'Entrega confirmada' : 'Destinatário não encontrado'}</strong><time>{new Date(attempt.attemptedAt).toLocaleString('pt-BR')}</time>{attempt.receiverName ? <p>Recebido por {attempt.receiverName} · {relationshipLabels[attempt.receiverRelationship ?? ''] ?? attempt.receiverRelationship}</p> : null}{attempt.notes ? <small>{attempt.notes}</small> : null}</div>{attempt.proofUrl ? <a href={attempt.proofUrl} target="_blank" rel="noreferrer"><img className="proof-image" src={attempt.proofUrl} alt="Foto registrada na entrega" /></a> : null}</article>)}</div> : <p className="muted-detail">Nenhuma tentativa registrada.</p>}</section>
        </div><footer className="modal-footer order-lifecycle-footer">{detail.deletedAt || detail.status === 'CANCELLED' ? <Button onClick={() => void restoreOrder()} loading={deleting}><RotateCcw size={15} /> Restaurar pedido</Button> : detail.status !== 'DELIVERED' ? <Button variant="danger" onClick={() => { setDeleteReason(''); setEditError(''); setDeleteOpen(true) }}><Trash2 size={15} /> Excluir da operação</Button> : <span className="order-preserved-note">Entregas concluídas são preservadas.</span>}</footer></>}
      </Modal> : null}
    </div>
  )
}
